// Resource definitions. The three basic resources (wood, stone, ore) are the
// hand-harvested ones that spawn as world nodes; ore is "deeper" so its nodes
// have more HP and demand more total damage. Knowledge is not harvested, it is
// produced by schools and gates age advancement.
//
// Run currency (spoils) and meta currency (skill points) are tracked as their
// own top-level state fields rather than here, because they reset on different
// rhythms (spoils each run, skill points never).

export type ResourceId = 'wood' | 'stone' | 'ore' | 'knowledge';

export interface ResourceDef {
  id: ResourceId;
  name: string;
  icon: string; // emoji used in the HUD and floaters
  /** Display tint as a 0xRRGGBB number, reused for the node sprite and the
   *  floating pickup number so colour reads consistently. */
  color: number;
  /** True for resources that spawn as harvestable world nodes. */
  harvestable: boolean;
  /** Node hit points at tier 1 (only meaningful when harvestable). */
  nodeHp: number;
  /** Resource granted when a node is fully depleted. */
  dropAmount: number;
  /** Seconds a depleted node takes to respawn. */
  respawn: number;
  /** Display ordering in the HUD. */
  order: number;
}

export const RESOURCES: Record<ResourceId, ResourceDef> = {
  wood: {
    id: 'wood',
    name: 'Wood',
    icon: '🪵',
    color: 0x9c6b3f,
    harvestable: true,
    nodeHp: 14,
    dropAmount: 4,
    respawn: 2.4,
    order: 0,
  },
  stone: {
    id: 'stone',
    name: 'Stone',
    icon: '🪨',
    color: 0x9aa3ad,
    harvestable: true,
    nodeHp: 28,
    dropAmount: 5,
    respawn: 3.2,
    order: 1,
  },
  ore: {
    id: 'ore',
    name: 'Ore',
    icon: '⛏️',
    color: 0xd98b46,
    harvestable: true,
    nodeHp: 60,
    dropAmount: 6,
    respawn: 4.5,
    order: 2,
  },
  knowledge: {
    id: 'knowledge',
    name: 'Knowledge',
    icon: '📚',
    color: 0x8e7bff,
    harvestable: false,
    nodeHp: 0,
    dropAmount: 0,
    respawn: 0,
    order: 3,
  },
};

/** Resource ids in stable display order. */
export const RESOURCE_ORDER: ResourceId[] = (Object.values(RESOURCES) as ResourceDef[])
  .sort((a, b) => a.order - b.order)
  .map((r) => r.id);

/** Just the harvestable resource ids (used to seed world nodes). */
export const HARVESTABLE_IDS: ResourceId[] = RESOURCE_ORDER.filter(
  (id) => RESOURCES[id].harvestable,
);
